import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Search, X } from 'lucide-react';

interface PanelSectionProps {
  title: string;
  count?: ReactNode;
  /** Small buttons on the right of the title row. */
  actions?: ReactNode;
  /** Rows that stay pinned under the title while the content scrolls (search, column headers). */
  toolbar?: ReactNode;
  children: ReactNode;
}

/** A section inside the side panel: sticky title (+ toolbar) over content that scrolls with the panel. */
export function PanelSection({ title, count, actions, toolbar, children }: PanelSectionProps) {
  return (
    <section className="min-h-full flex flex-col">
      <header className="sticky top-0 z-10 bg-[#232323] border-b border-white/5">
        <div className="flex items-center gap-2 h-11 px-4">
          <h2 className="text-[13px] font-semibold text-white truncate">{title}</h2>
          {count != null && <span className="text-[12px] text-gray-400 tabular-nums">{count}</span>}
          {actions && <div className="ml-auto flex items-center gap-1">{actions}</div>}
        </div>
        {toolbar}
      </header>
      <div className="flex-1">{children}</div>
    </section>
  );
}

interface PanelSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

export function PanelSearch({ value, onChange, placeholder }: PanelSearchProps) {
  const { t } = useTranslation();
  return (
    <label className="mx-4 mb-2.5 flex items-center gap-2 h-8 rounded-md bg-black/60 border border-white/10 px-2.5 transition-colors focus-within:border-lime-400/60">
      <Search size={14} className="shrink-0 text-gray-500" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="flex-1 min-w-0 bg-transparent text-[13px] text-white outline-none placeholder:text-gray-500"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label={t('workspace.clearSearch')}
          className="text-gray-500 hover:text-white"
        >
          <X size={14} />
        </button>
      )}
    </label>
  );
}

/** Small square icon button for section title rows. */
export function PanelIconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="h-7 w-7 rounded-md flex items-center justify-center text-gray-300 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}
